import type { IconTypes } from "solid-icons";
import {
  TbBook,
  TbBrain,
  TbBulb,
  TbChartBar,
  TbCompass,
  TbCornerDownRight,
  TbMessage,
  TbPuzzle,
  TbRocket,
  TbSearch,
  TbTarget,
} from "solid-icons/tb";
import { createEffect, createSignal, For, type JSX, onCleanup, Show } from "solid-js";
import { isServer } from "solid-js/web";
import { type FollowUpSuggestion, useChatRuntime } from "../runtime/ChatRuntime";

const SUGGESTION_ICONS: Record<string, IconTypes> = {
  search: TbSearch,
  chart: TbChartBar,
  bulb: TbBulb,
  compass: TbCompass,
  message: TbMessage,
  brain: TbBrain,
  target: TbTarget,
  puzzle: TbPuzzle,
  rocket: TbRocket,
  book: TbBook,
};

export function getSuggestionIcon(iconName?: string): IconTypes {
  return SUGGESTION_ICONS[iconName ?? ""] ?? TbBulb;
}

/** Plain suggestion text: ellipsis one line; full wrap when row hovered or focused */
export const followUpPlainTextCollapsedClass =
  "block w-full min-w-0 truncate text-left group-hover:whitespace-normal group-hover:overflow-visible group-hover:text-clip group-focus-within:whitespace-normal group-focus-within:overflow-visible group-focus-within:text-clip";

export const followUpRowExpandableBodyClass =
  "min-w-0 flex-1 overflow-hidden text-left transition-[color] duration-150 group-hover:overflow-visible group-focus-within:overflow-visible";

export const followUpChipRowBaseClass =
  "group text-left text-sm px-2.5 py-1.5 rounded-lg bg-transparent hover:bg-tile-hover text-secondary-text hover:text-primary-text transition-colors cursor-pointer select-none flex items-start gap-2 w-full min-w-0 hover:[&_[data-variable-v1-pill]]:grayscale-0 focus-within:[&_[data-variable-v1-pill]]:grayscale-0";

export const FollowUpSectionIcon = () => <TbCornerDownRight class="size-3.5 shrink-0 text-muted-text" />;

const FollowUpChipRow = (props: {
  suggestion: FollowUpSuggestion;
  onSelect: (s: FollowUpSuggestion) => void;
  renderBody?: (s: FollowUpSuggestion) => JSX.Element | undefined;
}) => {
  const body = () => (
    <>
      {(() => {
        const Icon = getSuggestionIcon(props.suggestion.icon);
        return <Icon size={14} class="mt-0.5 shrink-0 text-brand" />;
      })()}
      <div class={followUpRowExpandableBodyClass}>
        <Show
          when={props.renderBody?.(props.suggestion)}
          fallback={<span class={followUpPlainTextCollapsedClass}>{props.suggestion.text}</span>}
        >
          {(rendered) => rendered()}
        </Show>
      </div>
    </>
  );

  return (
    <button
      type="button"
      aria-label={props.suggestion.text}
      onClick={() => props.onSelect(props.suggestion)}
      class={followUpChipRowBaseClass}
    >
      {body()}
    </button>
  );
};

/**
 * The "Recommended follow-ups" section under the newest answer: header + one
 * chip per suggestion. Hosts own the data (bluesky: SSE-nudged store; chief:
 * the fetched wrapper below) and what a click does (insert into the composer).
 * `renderBody` lets bluesky swap in its rich-text pill body for suggestions
 * that reference skills/agents; returning undefined falls back to plain text.
 */
export const FollowUpSuggestionList = (props: {
  suggestions: FollowUpSuggestion[];
  onSelect: (suggestion: FollowUpSuggestion) => void;
  renderBody?: (suggestion: FollowUpSuggestion) => JSX.Element | undefined;
}) => {
  const rt = useChatRuntime();
  const select = (suggestion: FollowUpSuggestion) => {
    rt.actions.trackFeature?.({ feature: "chat.follow_up", via: "button" });
    props.onSelect(suggestion);
  };
  return (
    <div class="mt-3 duration-300">
      <div class="mb-1 flex items-center gap-2 px-2.5">
        <FollowUpSectionIcon />
        <span class="text-left text-xs font-medium text-muted-text">{rt.t("chat.followup_suggestions.title")}</span>
      </div>
      <div class="flex flex-col gap-0.5">
        <For each={props.suggestions}>
          {(suggestion) => <FollowUpChipRow suggestion={suggestion} onSelect={select} renderBody={props.renderBody} />}
        </For>
      </div>
    </div>
  );
};

/**
 * The host's backend holds the request open while suggestions generate, so an
 * empty answer is final. A rejection means a dropped connection and gets one
 * retry unless `signal` aborted it.
 */
export const loadFollowUps = (
  fetchFollowUps: (chatId: string, messageId: string, signal: AbortSignal) => Promise<FollowUpSuggestion[]>,
  chatId: string,
  messageId: string,
  signal: AbortSignal
): Promise<FollowUpSuggestion[]> =>
  fetchFollowUps(chatId, messageId, signal).catch(() =>
    signal.aborted ? [] : fetchFollowUps(chatId, messageId, signal).catch(() => [])
  );

/**
 * Follow-up chips over the `fetchFollowUps` seam for hosts without an SSE bus
 * (chief). Shows only under the newest message; a click prefills the composer
 * via `insertPromptText` (matching the web's insert-don't-submit behavior).
 */
export const FetchedFollowUpChips = (props: { chatId: string; messageId: string; isLastMessage: boolean }) => {
  const rt = useChatRuntime();
  const [suggestions, setSuggestions] = createSignal<FollowUpSuggestion[]>([]);

  createEffect(() => {
    const messageId = props.messageId;
    const fetchFollowUps = rt.actions.fetchFollowUps;
    setSuggestions([]);
    if (isServer || !props.isLastMessage || !fetchFollowUps) return;

    const aborter = new AbortController();
    const load = async (): Promise<void> => {
      const got = await loadFollowUps(fetchFollowUps, props.chatId, messageId, aborter.signal);
      if (!aborter.signal.aborted) setSuggestions(got);
    };
    void load();
    onCleanup(() => aborter.abort());
  });

  const insert = (s: FollowUpSuggestion) => rt.actions.insertPromptText?.(s.text);

  return (
    <Show when={props.isLastMessage && suggestions().length > 0}>
      <FollowUpSuggestionList suggestions={suggestions()} onSelect={insert} />
    </Show>
  );
};
